/**
 * Prefiksowane, brandowane identyfikatory.
 *
 * Kod dzielony miedzy przegladarka i Workerem — zero DOM, zero Cloudflare.
 *
 * Kazdy identyfikator ma postac `<prefix>_<21 znakow nanoid>`, np.
 * `acc_V1StGXR8Z5jdHi6BmyT8s`. Prefiks jest czescia typu (brand), wiec
 * `AccountId` nie jest przypisywalny tam gdzie oczekiwany jest `UserId`,
 * mimo ze oba sa w runtime zwyklymi stringami.
 */

import { nanoid } from 'nanoid';

/** Separator miedzy prefiksem a czescia losowa. */
export const ID_SEPARATOR = '_';

/** Dlugosc czesci losowej (domyslna dlugosc nanoid). */
export const ID_RANDOM_LENGTH = 21;

/**
 * Mapa nazwa-domenowa -> prefiks. Zrodlo prawdy dla calego repo;
 * nie hardkoduj stringow prefiksow nigdzie indziej.
 */
export const ID_PREFIXES = {
  account: 'acc',
  user: 'usr',
  client: 'cli',
  category: 'ctg',
  product: 'prd',
  productImage: 'pim',
  preset: 'pst',
  scene: 'scn',
  sceneRevision: 'rev',
  generation: 'gen',
  variant: 'var',
  asset: 'ast',
  caption: 'cap',
  shareLink: 'shr',
} as const;

export type IdEntity = keyof typeof ID_PREFIXES;

/** Union wszystkich dozwolonych prefiksow: 'acc' | 'usr' | ... */
export type IdPrefix = (typeof ID_PREFIXES)[IdEntity];

/** Lista prefiksow, posortowana leksykograficznie dla determinizmu. */
export const ID_PREFIX_LIST: readonly IdPrefix[] = Object.values(ID_PREFIXES)
  .slice()
  .sort() as readonly IdPrefix[];

const ID_PREFIX_SET: ReadonlySet<string> = new Set<string>(ID_PREFIX_LIST);

declare const idBrand: unique symbol;

/** Brandowany identyfikator o danym prefiksie. */
export type Id<P extends IdPrefix> = string & { readonly [idBrand]: P };

export type AccountId = Id<'acc'>;
export type UserId = Id<'usr'>;
export type ClientId = Id<'cli'>;
export type CategoryId = Id<'ctg'>;
export type ProductId = Id<'prd'>;
export type ProductImageId = Id<'pim'>;
export type PresetId = Id<'pst'>;
export type SceneId = Id<'scn'>;
export type SceneRevisionId = Id<'rev'>;
export type GenerationId = Id<'gen'>;
export type VariantId = Id<'var'>;
export type AssetId = Id<'ast'>;
export type CaptionId = Id<'cap'>;
export type ShareLinkId = Id<'shr'>;

/** Dowolny identyfikator aplikacji, gdy konkretny prefiks nie ma znaczenia. */
export type AnyId = Id<IdPrefix>;

/**
 * Alfabet nanoid: A-Za-z0-9 oraz `-` i `_`. Czesc losowa moze zawierac `_`,
 * dlatego walidacja i parsowanie dziela string na PIERWSZYM separatorze.
 */
const RANDOM_PART = /^[A-Za-z0-9_-]+$/;

/** Nowy identyfikator dla podanego prefiksu. */
export function newId<P extends IdPrefix>(prefix: P): Id<P> {
  return `${prefix}${ID_SEPARATOR}${nanoid(ID_RANDOM_LENGTH)}` as Id<P>;
}

/** Nowy identyfikator dla nazwy domenowej, np. `newEntityId('sceneRevision')`. */
export function newEntityId<E extends IdEntity>(entity: E): Id<(typeof ID_PREFIXES)[E]> {
  return newId(ID_PREFIXES[entity]);
}

/** Prefiks identyfikatora albo `null`, gdy string nie jest identyfikatorem. */
export function idPrefixOf(value: unknown): IdPrefix | null {
  if (typeof value !== 'string') return null;
  const cut = value.indexOf(ID_SEPARATOR);
  if (cut <= 0) return null;
  const prefix = value.slice(0, cut);
  if (!ID_PREFIX_SET.has(prefix)) return null;
  const random = value.slice(cut + ID_SEPARATOR.length);
  if (random.length !== ID_RANDOM_LENGTH || !RANDOM_PART.test(random)) return null;
  return prefix as IdPrefix;
}

/** Typeguard: `value` jest identyfikatorem o prefiksie `prefix`. */
export function isId<P extends IdPrefix>(prefix: P, value: unknown): value is Id<P> {
  return idPrefixOf(value) === prefix;
}

/** Typeguard: `value` jest jakimkolwiek identyfikatorem aplikacji. */
export function isAnyId(value: unknown): value is AnyId {
  return idPrefixOf(value) !== null;
}

/** Rzuca `TypeError`, gdy `value` nie jest identyfikatorem o prefiksie `prefix`. */
export function assertId<P extends IdPrefix>(prefix: P, value: unknown): Id<P> {
  if (!isId(prefix, value)) {
    throw new TypeError(`Expected id with prefix "${prefix}${ID_SEPARATOR}", got: ${describe(value)}`);
  }
  return value;
}

/**
 * Rzutowanie bez walidacji — wylacznie dla wierszy przeczytanych z D1, gdzie
 * schemat gwarantuje poprawnosc. Na danych z zewnatrz uzyj `assertId`.
 */
export function unsafeId<P extends IdPrefix>(prefix: P, value: string): Id<P> {
  void prefix;
  return value as Id<P>;
}

function describe(value: unknown): string {
  return typeof value === 'string' ? JSON.stringify(value) : String(value);
}

// Typeguardy nazwane — cukier na `isId(...)`, przydatny w walidacji DTO.
export const isAccountId = (v: unknown): v is AccountId => isId('acc', v);
export const isUserId = (v: unknown): v is UserId => isId('usr', v);
export const isClientId = (v: unknown): v is ClientId => isId('cli', v);
export const isCategoryId = (v: unknown): v is CategoryId => isId('ctg', v);
export const isProductId = (v: unknown): v is ProductId => isId('prd', v);
export const isProductImageId = (v: unknown): v is ProductImageId => isId('pim', v);
export const isPresetId = (v: unknown): v is PresetId => isId('pst', v);
export const isSceneId = (v: unknown): v is SceneId => isId('scn', v);
export const isSceneRevisionId = (v: unknown): v is SceneRevisionId => isId('rev', v);
export const isGenerationId = (v: unknown): v is GenerationId => isId('gen', v);
export const isVariantId = (v: unknown): v is VariantId => isId('var', v);
export const isAssetId = (v: unknown): v is AssetId => isId('ast', v);
export const isCaptionId = (v: unknown): v is CaptionId => isId('cap', v);
export const isShareLinkId = (v: unknown): v is ShareLinkId => isId('shr', v);
